import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';

// M5.14 (polish): team activity feed view for admins.
//
// Operator's use case: a consolidated view of what
// people are getting per team, with an expanded list of
// the last N hours of events that triggered for them.
// Defaults to 2h; the hard-coded dropdown lets the
// operator flip between 1h / 2h / 4h / 8h / 24h without
// navigating away.
//
// "Credited" events only — the filter is applied at the
// api level (events with at least one unvoided xp_award
// OR one unvoided earn coin_transaction). "Happened but
// no award" events (rate-limited, deduped, criteria-not-
// met) are excluded because the operator's mental model
// is "what did this person earn and from which events".

interface UserEvent {
  id: string;
  occurred_at: string;
  type: string;
  ref: string;
  xp: number;
  coins: number;
  source: string;
  rule_description: string | null;
  awards: { competency_id: string; xp: number }[];
}

interface UserActivity {
  user_id: string;
  handle: string;
  display_name: string;
  email: string;
  total_xp: number;
  total_coins: number;
  event_count: number;
  events: UserEvent[];
}

interface TeamActivity {
  team_id: string;
  team_name: string;
  window_hours: number;
  generated_at: string;
  users: UserActivity[];
}

interface Team {
  id: string;
  name: string;
  slug: string;
}

const WINDOW_OPTIONS = [1, 2, 4, 8, 24];

export function TeamActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useQuery<{ teams: Team[] }>({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';
  const setTeamId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team_id', id); else next.delete('team_id');
    setSearchParams(next, { replace: true });
  };
  const hoursParam = Number(searchParams.get('hours') ?? '2');
  const hours = WINDOW_OPTIONS.includes(hoursParam) ? hoursParam : 2;
  const setHours = (h: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('hours', String(h));
    setSearchParams(next, { replace: true });
  };

  const activity = useQuery<TeamActivity>({
    queryKey: ['team-activity', teamId, hours],
    queryFn: () => api<TeamActivity>(`/api/admin/teams/${teamId}/activity?hours=${hours}`),
    enabled: !!teamId,
    // Persist the result for a few seconds so flipping
    // between window options doesn't show a flash of
    // "loading". The actual data is fresh enough at
    // 30s; the page is a snapshot, not a feed.
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Admin</Link>
          <h1 className="mt-1 text-2xl font-semibold">Team activity</h1>
          <p className="text-ink-muted">
            What each team member has earned in the last{' '}
            <strong className="text-ink">{hours}h</strong> — events grouped by user, awarded XP + coins only.
          </p>
        </div>
        {/* Controls: team picker + window picker. Same
            shape as the existing team-scoped admin pages
            (e.g. /admin/heatmap, /admin/teams/:teamId/rewards). */}
        <div className="flex items-center gap-2">
          <label htmlFor="team-activity-team" className="text-xs text-ink-muted">Team</label>
          <select
            id="team-activity-team"
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <label htmlFor="team-activity-hours" className="ml-2 text-xs text-ink-muted">Window</label>
          <select
            id="team-activity-hours"
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
          >
            {WINDOW_OPTIONS.map((h) => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
        </div>
      </header>

      {activity.isLoading ? (
        <p className="text-ink-muted">Loading…</p>
      ) : activity.isError ? (
        <p className="text-accent-danger">
          Failed to load: {activity.error instanceof ApiError ? activity.error.message : String(activity.error)}
        </p>
      ) : activity.data ? (
        <TeamActivityView data={activity.data} />
      ) : null}
    </div>
  );
}

function TeamActivityView({ data }: { data: TeamActivity }) {
  if (data.users.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-ink-muted">
          No team members. The team exists but has no one on it.
        </p>
      </div>
    );
  }
  // Aggregate totals across the team. The same numbers
  // are also shown per-user; the team total is the
  // headline for the operator's quick scan.
  const totals = data.users.reduce(
    (s, u) => ({ xp: s.xp + u.total_xp, coins: s.coins + u.total_coins, events: s.events + u.event_count }),
    { xp: 0, coins: 0, events: 0 },
  );
  return (
    <>
      <div className="card flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="text-ink-muted">Team</span>
        <strong className="text-base">{data.team_name}</strong>
        <span className="text-ink-muted">·</span>
        <span>{data.users.length} members</span>
        <span className="text-ink-muted">·</span>
        <span className="font-mono text-accent-success">+{totals.xp} XP</span>
        <span className="text-ink-muted">·</span>
        <span className="font-mono text-accent-warning">+{totals.coins} 🪙</span>
        <span className="text-ink-muted">·</span>
        <span>{totals.events} events</span>
      </div>
      <div className="space-y-2">
        {data.users.map((u) => (
          <UserActivityCard key={u.user_id} user={u} />
        ))}
      </div>
    </>
  );
}

function UserActivityCard({ user }: { user: UserActivity }) {
  // Default expanded so the operator's quick-scan reads
  // top-to-bottom without a click. The header shows the
  // totals so collapsing doesn't lose the headline.
  const [open, setOpen] = useState(true);
  return (
    <div className="card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <div className="flex items-baseline gap-2">
          <span className="font-medium">{user.display_name}</span>
          <span className="text-xs text-ink-dim">@{user.handle}</span>
          {user.event_count === 0 ? (
            <span className="text-xs text-ink-dim">— nothing in the last {user.event_count}h</span>
          ) : (
            <span className="text-xs text-ink-dim">— {user.event_count} events</span>
          )}
        </div>
        <div className="flex items-baseline gap-3">
          {user.total_xp > 0 ? (
            <span className="font-mono text-sm text-accent-success">+{user.total_xp} XP</span>
          ) : null}
          {user.total_coins > 0 ? (
            <span className="font-mono text-sm text-accent-warning">+{user.total_coins} 🪙</span>
          ) : null}
          <span className="text-xs text-ink-dim">{open ? '▼' : '▶'}</span>
        </div>
      </button>
      {open && user.events.length > 0 ? (
        <ul className="mt-3 space-y-1 text-sm">
          {user.events.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function EventRow({ event }: { event: UserEvent }) {
  // The row shape mirrors the player dashboard's event
  // feed for consistency: type · ref · XP · coins ·
  // competency breakdown. The "Xm ago" timestamp
  // replaces the absolute time so the operator can
  // scan "what happened in the last few minutes" at a
  // glance.
  return (
    <li className="rounded-md bg-bg-elevated px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-xs text-ink-muted">{event.type}</span>
        <span className="font-medium">{event.ref}</span>
        {event.xp > 0 ? (
          <span className="font-mono text-xs text-accent-success">+{event.xp} XP</span>
        ) : null}
        {event.coins > 0 ? (
          <span className="font-mono text-xs text-accent-warning">+{event.coins} 🪙</span>
        ) : null}
        <span className="ml-auto text-xs text-ink-dim" title={event.occurred_at}>
          {relativeTime(event.occurred_at)}
        </span>
      </div>
      {event.awards.length > 0 ? (
        <div className="mt-1 flex flex-wrap items-baseline gap-2 text-xs text-ink-muted">
          {event.awards.map((a, i) => (
            <span key={i} className="font-mono">+{a.xp} {a.competency_id}</span>
          ))}
        </div>
      ) : null}
      {event.rule_description ? (
        <p className="mt-1 text-xs text-ink-dim">{event.rule_description}</p>
      ) : null}
    </li>
  );
}

/**
 * Relative time helper. Less than 60s = "Ns",
 * less than 60m = "Nm", less than 24h = "Nh",
 * otherwise "Nd". Mirrors the simple format the
 * live feed panel uses for "Xm ago" timestamps.
 */
function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
